import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    HeatmapCellType,
    HeatmapCellPosition,
    HeatmapPercentile,
    HeatmapRisk,
    HeatmapScoreType,
    HEATMAP_RISK_TYPE_TO_TITLE,
} from './HeatmapTypes';
import HeatmapGrid from './HeatmapGrid';
import APIService, {
    E_RISK_TYPE,
    IActiveDimension,
    IActiveDimensionTree,
    IActiveCountry,
    IActiveClientFacingIndicator,
    IActiveGroup,
} from 'services/APIService';
import {
    map,
    get,
    isEmpty,
    filter,
    flatten,
    values,
    flow,
    sortBy,
    uniqBy,
    find,
    findIndex,
    includes,
    keyBy,
} from 'lodash/fp';
import moment from 'moment';
import { C_SCORES_DATE_FORMAT, TIME_WINDOW_PRESETS } from '../../constants';
import HeatmapTooltip from './HeatmapTooltip';
import GQLoader from 'components/GQLoader/GQLoader';
import { HEATMAP_PERCENTILES } from './HeatmapPercentiles';
import { inject } from 'mobx-react';
import { useSearchParams } from 'react-router-dom';
import { IMobxRootState } from '../../RootStore';
import heatmapStore from '../../stores/HeatmapStore';
import { sortCountriesByName } from '../../utils/countryUtils';
import { isHeatmapPercentileVisible } from 'utils/generalUtils';
import loading from '../../services/LoadingService';
import { getRiskLongName } from 'utils/riskUtils';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { IPreloadedHeatmapApiData } from '../GQTemplateV2/exportInterfaces';
import HeatmapToolbar from 'components/Heatmap/HeatmapToolbar';

const redPercentiles = filter(
    (percentile: HeatmapPercentile) => percentile.type === 'red',
    HEATMAP_PERCENTILES
).sort((p1, p2) => p2.percentile - p1.percentile);

const greenPercentiles = filter(
    (percentile: HeatmapPercentile) => percentile.type === 'green',
    HEATMAP_PERCENTILES
).sort((p1, p2) => p2.percentile - p1.percentile);

export const getHeatmapPercentile = (
    percentileValue: number
): HeatmapPercentile => {
    const isRiskRising = percentileValue > 0;
    const valueAbs = Math.abs(percentileValue);
    const percentilesGroup = isRiskRising ? redPercentiles : greenPercentiles;
    for (let percentile of percentilesGroup) {
        if (valueAbs >= percentile.percentile) {
            return percentile;
        }
    }
    return null;
};

const getListFromTree = (
    dimensionsTree: IActiveDimensionTree
): IActiveDimension[] => {
    return [
        dimensionsTree,
        ...flatten(
            map(
                (childTree: IActiveDimensionTree) => getListFromTree(childTree),
                dimensionsTree.children
            )
        ),
    ];
};

interface IHeatmapProviderProps {
    visibleCountryIds?: number[];
    minDate?: number;
    numDays?: number;
    selectedCountryId?: number;
    riskType?: E_RISK_TYPE;
    scoreType?: HeatmapScoreType;
    selectedRisk?: number;
    activeGroupId?: string;
    disableClickOutside?: boolean;
    disableTooltip?: boolean;
    disableActiveGroup?: boolean;
    disableToggle?: boolean;
    selectedPercentile?: HeatmapPercentile;
    isAnalyst?: boolean;
    hideToolbar?: boolean;
    preloadedData?: IPreloadedHeatmapApiData;

}

export const HeatmapProvider = inject(
    ({ heatmapStore, settingsStore, UserStore }: IMobxRootState) => {
        return {
            visibleCountryIds: heatmapStore.countriesList,
            selectedCountryId: heatmapStore.selectedCountry,
            riskType: heatmapStore.riskType,
            scoreType: heatmapStore.scoreType,
            selectedRisk: heatmapStore.selectedRisk,
            minDate: heatmapStore.startDate,
            numDays: TIME_WINDOW_PRESETS[heatmapStore.timeSpan].val,
            selectedPercentile: heatmapStore.selectedPercentile,
            activeGroupId: settingsStore.activeGroupId,
            isAnalyst: UserStore.geoquantFlagsPermission.analyst,
        };
    }
)(
    ({
        visibleCountryIds,
        selectedRisk,
        riskType,
        selectedCountryId,
        minDate,
        numDays,
        selectedPercentile,
        activeGroupId,
        scoreType,
        disableClickOutside,
        disableTooltip,
        disableActiveGroup,
        isAnalyst,
        hideToolbar,
        preloadedData,
    }: IHeatmapProviderProps) => {
        const [isInitialized, setIsInitialized] = useState<boolean>(false);

        // When the URL already specifies the heatmap countries (deep-link /
        // refresh), the URL is the source of truth: skip the one-time active
        // group initializer below so it doesn't clobber the hydrated selection.
        const [searchParams] = useSearchParams();
        const skipInitialGroupRef = useRef(Boolean(searchParams.get('countries')));

        const [heatmapCells, setHeatmapCells] = useState<HeatmapCellType[][]>(
            []
        );
        const [dimensionsList, setDimensionsList] = useState<
            IActiveDimension[]
        >(null);
        const [clientFacingIndicators, setClientFacingIndicators] = useState<
            IActiveClientFacingIndicator[]
        >(null);
        const [allCountries, setAllCountries] = useState<IActiveCountry[]>(
            null
        );
        const [hoveredCell, setHoveredCell] = useState<HeatmapCellPosition>(
            null
        );
        const [thresholdsData, setThresholdsData] = useState<
            HeatmapCellType[][]
        >([]);

        const [allCountriesById, allCountriesByName] = useMemo(() => {
            return [
                keyBy(country => country.id, allCountries),
                keyBy(country => country.name, allCountries),
            ];
        }, [allCountries]);

        const countryNames = useMemo(() => {
            return flow(
                filter((country: IActiveCountry) =>
                    includes(country.id, visibleCountryIds)
                ),
                map(get('name'))
            )(allCountries);
        }, [allCountries, visibleCountryIds]);

        const startDate = useMemo(
            () => moment(minDate || moment()).format(C_SCORES_DATE_FORMAT),
            [minDate]
        );

        useEffect(() => {
            (async () => {
                const [
                    indicators,
                    dimensionsTree,
                    activeCountries,
                ] = preloadedData
                    ? [
                          preloadedData.indicators,
                          preloadedData.dimensionsTree,
                          preloadedData.activeCountries,
                      ]
                    : await Promise.all([
                          APIService.getActiveClientFacingIndicators(),
                          APIService.getActiveDimensionsTree(),
                          APIService.getActiveCountries(),
                      ]);

                const countries = sortCountriesByName(
                    values(activeCountries.licensed)
                );
                setAllCountries(countries);

                const clientFacingIndicators = flow(
                    values,
                    filter(
                        (indicator: IActiveClientFacingIndicator) =>
                            !indicator.disabled
                    ),
                    sortBy(indicator => indicator.order)
                )(indicators);
                setClientFacingIndicators(clientFacingIndicators);

                //todo: remove uniqBy and use parentId or a backend unique id
                const dimensionsList = uniqBy(
                    'id',
                    getListFromTree(dimensionsTree)
                );
                setDimensionsList(dimensionsList);

                setIsInitialized(true);
            })();
        }, [preloadedData]);

        useEffect(() => {
            if (disableActiveGroup) {
                return;
            }

            // On the first run after a deep-link/refresh that carried a
            // `countries` URL param, the hydrated selection wins — skip once.
            if (skipInitialGroupRef.current) {
                skipInitialGroupRef.current = false;
                return;
            }

            APIService.getActiveGroups().then(activeGroups => {
                const activeGroupCountriesList = flow(
                    get('groups'),
                    find((group: IActiveGroup) => group.id === activeGroupId),
                    get('countries')
                )(activeGroups);

                if (!isEmpty(activeGroupCountriesList)) {
                    heatmapStore.setCountriesList(activeGroupCountriesList);
                }
            });
        }, [activeGroupId, disableActiveGroup]);

        useEffect(() => {
            if (!isInitialized) {
                return;
            }
            heatmapStore.setSelectedPercentile(null);
            heatmapStore.resetSelection();
            fetchHeatmap(riskType, scoreType);
        }, [isInitialized, riskType, scoreType, startDate, numDays, visibleCountryIds]);

        const fetchHeatmap = async (
            riskType: E_RISK_TYPE,
            scoreType: HeatmapScoreType
        ) => {
            if (isEmpty(visibleCountryIds)) {
                setThresholdsData([]);
                return;
            }

            loading.start();
            const percentilesData = preloadedData
                ? preloadedData.percentilesData
                : await APIService.getHeatmap(
                      riskType,
                      scoreType,
                      startDate,
                      numDays,
                      visibleCountryIds
                  );

            const risks: HeatmapRisk[] =
                riskType === E_RISK_TYPE.CLIENT_FACING_INDICATORS
                    ? map(
                          indicator => ({
                              id: indicator.id,
                              title: indicator.display_name,
                          }),
                          clientFacingIndicators
                      )
                    : map(
                          dimension => ({
                              id: dimension.id,
                              title: dimension.lname,
                          }),
                          dimensionsList
                      );

            const thresholdsData = map(countryName => {
                return map(risk => {
                    const country = get(countryName, allCountriesByName);
                    const percentileValue =
                        get([country.id, risk.id], percentilesData) || 1;
                    const percentile: HeatmapPercentile = getHeatmapPercentile(
                        percentileValue
                    );

                    return {
                        value: percentileValue,
                        color: get('color', percentile),
                        isVisible: !!percentile,
                        clickable: !!percentile,
                        percentile,
                        tooltip:
                            !disableTooltip &&
                            (() => (
                                <HeatmapTooltip
                                    country={country}
                                    risk={risk}
                                    riskType={riskType}
                                    scoreType={scoreType}
                                    minDate={minDate}
                                    numDays={numDays}
                                    isAnalyst={isAnalyst}
                                />
                            )),
                    } as HeatmapCellType;
                }, risks);
            }, countryNames);
            setThresholdsData(thresholdsData);
            setIsInitialized(true);
            loading.stop();
        };

        useEffect(() => {
            const riskIndex =
                riskType === E_RISK_TYPE.DIMENSIONS
                    ? findIndex(
                          (dimension: IActiveDimension) =>
                              dimension.id === selectedRisk,
                          dimensionsList
                      )
                    : findIndex(
                          (indicator: IActiveClientFacingIndicator) =>
                              indicator.id === selectedRisk,
                          clientFacingIndicators
                      );

            const selectedCountryName = get(
                [selectedCountryId, 'name'],
                allCountriesById
            );
            const countryIndex = findIndex(
                (countryName: string) => countryName === selectedCountryName,
                countryNames
            );

            const cells = thresholdsData.map(
                (row: HeatmapCellType[], rowIndex: number) =>
                    row.map((cell: HeatmapCellType, columnIndex: number) => {
                        if (!cell.percentile) {
                            return {
                                ...cell,
                                isVisible: false,
                            };
                        }

                        if (
                            isHeatmapPercentileVisible(
                                cell.percentile,
                                selectedPercentile
                            ) ||
                            riskIndex === columnIndex ||
                            countryIndex === rowIndex
                        ) {
                            return {
                                ...cell,
                                isVisible: true,
                            };
                        }
                        return {
                            ...cell,
                            isVisible:
                                !selectedPercentile &&
                                riskIndex === -1 &&
                                countryIndex === -1,
                        };
                    })
            );
            setHeatmapCells(cells);
        }, [
            allCountriesById,
            clientFacingIndicators,
            countryNames,
            dimensionsList,
            riskType,
            selectedCountryId,
            selectedPercentile,
            selectedRisk,
            thresholdsData,
        ]);

        useEffect(() => {
            if (!selectedRisk) {
                heatmapStore.setTitle(HEATMAP_RISK_TYPE_TO_TITLE[riskType]);
                return;
            }
            const selectedRiskName = getRiskLongName(riskType, selectedRisk);
            heatmapStore.setTitle(selectedRiskName);
        }, [riskType, selectedRisk, clientFacingIndicators, dimensionsList]);

        const onClickOutside = useCallback(
            (event: any) => {
                if (disableClickOutside) {
                    return;
                }
                const headerElement = document.querySelector('.picker-toggler');
                const clickedInsideHeader = headerElement.contains(
                    event.target
                );
                // without the following 'if' - clicking a map view link on
                // the header will cause the selected country+risk to be reset,
                // but this is bad since the map view should remember the
                // heatmap's selected country+risk
                if (clickedInsideHeader) {
                    return;
                }

                heatmapStore.setSelectedPercentile(null);
                heatmapStore.resetSelection();
            },
            [disableClickOutside]
        );

        const onCellPress = useCallback(
            (rowIndex: number, columnIndex: number) => {
                if (selectedCountryId || selectedRisk) {
                    heatmapStore.resetSelection();
                    return;
                }

                const risks =
                    riskType === E_RISK_TYPE.DIMENSIONS
                        ? dimensionsList
                        : clientFacingIndicators;
                const selectedRiskId = get([columnIndex, 'id'], risks);
                const countryName = get(rowIndex, countryNames);
                const newCountryId = get(
                    [countryName, 'id'],
                    allCountriesByName
                );

                heatmapStore.setSelectedPercentile(null);
                heatmapStore.setSelectedRisk(selectedRiskId);
                heatmapStore.setSelectedCountry(newCountryId);
            },
            [
                selectedCountryId,
                selectedRisk,
                riskType,
                dimensionsList,
                clientFacingIndicators,
                countryNames,
                allCountriesByName,
            ]
        );

        const onCellMouseEnter = useCallback(
            (rowIndex: number, colIndex: number) => {
                setHoveredCell({
                    row: rowIndex,
                    column: colIndex,
                });
            },
            []
        );

        const onCellMouseLeave = useCallback(() => {
            setHoveredCell(null);
        }, []);

        const onCountryClick = useCallback(
            (countryName: string) => {
                const newCountryId = get(
                    [countryName, 'id'],
                    allCountriesByName
                );

                if (selectedCountryId === newCountryId) {
                    heatmapStore.setSelectedCountry(null);
                    return;
                }

                heatmapStore.setSelectedCountry(newCountryId);
                heatmapStore.setSelectedRisk(null);
                heatmapStore.setSelectedPercentile(null);
            },
            [allCountriesByName, selectedCountryId]
        );

        const onRiskClick = useCallback(
            (riskId: number) => {
                if (selectedRisk === riskId) {
                    heatmapStore.setSelectedRisk(null);
                    return;
                }

                heatmapStore.setSelectedRisk(riskId);
                heatmapStore.setSelectedCountry(null);
                heatmapStore.setSelectedPercentile(null);
            },
            [selectedRisk]
        );

        const onSelectPercentile = useCallback(
            (percentile: HeatmapPercentile) => {
                const didPressOnSamePercentile =
                    selectedPercentile &&
                    selectedPercentile.percentile === percentile.percentile;

                heatmapStore.setSelectedPercentile(
                    didPressOnSamePercentile ? null : percentile
                );

                heatmapStore.resetSelection();
            },
            [selectedPercentile]
        );

        const hoveredRiskId = useMemo(() => {
            const hoveredColumnIndex = get('column', hoveredCell);
            const riskId = get(
                [hoveredColumnIndex, 'id'],
                riskType === E_RISK_TYPE.DIMENSIONS
                    ? dimensionsList
                    : clientFacingIndicators
            );

            return riskId;
        }, [dimensionsList, hoveredCell, riskType, clientFacingIndicators]);

        const selectedCountryName = useMemo(
            () => get([selectedCountryId, 'name'], allCountriesById),
            [allCountriesById, selectedCountryId]
        );

        const clickRef = useOutsideClick(onClickOutside);

        if (isEmpty(heatmapCells) && !isEmpty(visibleCountryIds)) {
            return (
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <GQLoader />
                </div>
            );
        }

        return (
            <div ref={clickRef} style={{ display: 'contents' }}>
                {!hideToolbar && (
                    <HeatmapToolbar title={heatmapStore.title} />
                )}
                <HeatmapGrid
                    cells={heatmapCells}
                    percentiles={HEATMAP_PERCENTILES}
                    onCellPress={onCellPress}
                    onCellMouseEnter={onCellMouseEnter}
                    onCellMouseLeave={onCellMouseLeave}
                    dimensionsList={dimensionsList}
                    selectedPercentile={selectedPercentile}
                    onSelectPercentile={onSelectPercentile}
                    hoveredCell={hoveredCell}
                    selectedRiskId={selectedRisk}
                    hoveredRiskId={hoveredRiskId}
                    riskType={riskType}
                    scoreType={scoreType}
                    selectedCountry={selectedCountryName}
                    onCountryClick={onCountryClick}
                    countryNames={countryNames}
                    onDimensionClick={onRiskClick}
                    clientFacingIndicators={clientFacingIndicators}
                    numDays={numDays}
                />
            </div>
        );
    }
);